import React, { useContext, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { customization, dialogs, view, i18next } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import { camelCaseObjectToSnakeCase } from 'src/js/modules/utils/convert-case';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import triggerIds from 'src/js/modules/website-design/constants/trigger-ids';
import variableNames from 'src/js/modules/website-design/constants/variable-names';
import segment from 'src/js/modules/analytics/segment';
import getSurfaceName from 'src/js/modules/website-design/helpers/get-surface-name';
import getSafeColorScheme from 'src/js/modules/website-design/helpers/get-safe-color-scheme';
import { resolveVisualPreviewColor } from 'src/js/modules/website-design/helpers/resolve-visual-preview-color';
import { INDETERMINATE } from '../constants/common';
import ElementSettingsContext from '../helpers/element-settings-context';
import getBatchValues from '../helpers/get-batch-values';

const { ToggleSection, Box, ColorTileSelector } = designSystem;

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const buildChoices = (choices, palette) => {
  const colors = palette?.colors || {};
  const schemes = palette?.schemes || {};

  const baseChoices = choices.map((choice) => {
    const scheme = schemes[choice.value];
    const color =
      scheme?.variableOverrides?.[variableNames.BLOCK_BACKGROUND] ||
      scheme?.baseColor ||
      colors[choice.value];
    const overrides = scheme?.variableOverrides || {};
    const { backgroundColor, isDarkTheme } = resolveVisualPreviewColor({
      color,
      overrides: camelCaseObjectToSnakeCase(overrides),
    });

    return {
      ...choice,
      backgroundColor,
      isDarkTheme,
      tooltipText: getSurfaceName(choice.value, schemes),
    };
  });

  const existingValues = new Set(baseChoices.map((choice) => choice.value));
  const customChoices = Object.entries(schemes)
    .filter(
      ([schemeId]) =>
        schemeId.startsWith(customization.constants.CUSTOM_COLOR_SCHEME_PREFIX) &&
        !existingValues.has(schemeId)
    )
    .map(([schemeId, scheme]) => {
      const color = scheme.variableOverrides?.[variableNames.BLOCK_BACKGROUND] || scheme.baseColor;
      const overrides = scheme?.variableOverrides || {};
      const { backgroundColor, isDarkTheme } = resolveVisualPreviewColor({
        color,
        overrides: camelCaseObjectToSnakeCase(overrides),
      });

      return {
        id: schemeId,
        value: schemeId,
        backgroundColor,
        isDarkTheme,
        tooltipText: scheme.label,
      };
    });

  return [...baseChoices, ...customChoices];
};

const getCaptions = () => ({
  addColorSchemeTooltipText: i18next.t('Add color scheme'),
  indeterminateToolTipText: i18next.t('Mixed'),
  toggleLabels: {
    expanded: i18next.t('Show less'),
    collapsed: i18next.t('Show more'),
  },
});

const getCurrentValue = (rawValues, palette) => {
  const values = rawValues.map((rawValue) => getSafeColorScheme(rawValue, palette));

  const isIndeterminate = values.some((value) => value !== values[0]);
  return isIndeterminate ? INDETERMINATE : String(values[0] || '');
};

function ColorSchemeControl({
  id,
  elementIds,
  attributes,
  title,
  choices,
  popover = null,
  onChange,
}) {
  const { state, sharedData } = useContext(ElementSettingsContext);
  const palette = customization.accessors.getCurrentColorPalette();

  const flattenedValues = getBatchValues(state, {
    elementIds,
    attributes,
  }).flat();
  const isToggleEnabled = flattenedValues.some(Boolean);

  const currentValue = getCurrentValue(flattenedValues, palette);
  const surfaceAttribute = attributes[0];

  const updatedChoices = useMemo(() => buildChoices(choices, palette), [choices, palette]);

  const handleChange = useCallback(
    (value) => {
      onChange({ controlId: id, elementIds, attributes, value });
    },
    [id, elementIds, attributes, onChange]
  );

  const handleToggleChange = useCallback(
    (value) => {
      let newValue = value;

      if (value) {
        if (!currentValue) {
          const element = view.accessors.getLiveElement(elementIds[0]);
          const elementWithSurface = element.closest('[surfaces]');
          const [surface] = elementWithSurface.getAttribute('surfaces').split(' ');

          newValue = surface;
        }
      } else {
        newValue = false;
      }

      handleChange(newValue);
    },
    [currentValue, elementIds, handleChange]
  );

  const handleColorSchemeChange = useCallback(
    (value) => {
      handleChange(value);
    },
    [handleChange]
  );

  const handleConfigureClick = useCallback(
    async (colorSchemeId) => {
      await sharedData.onPersist({}, { preservePosition: true });

      dialogs.operations.show(dialogTypes.ADVANCED_COLORS_SETTINGS_DIALOG, {
        colorSchemeId,
        triggerId: triggerIds.ELEMENT_SETTINGS,
      });
    },
    [sharedData]
  );

  const handleAddColorScheme = useCallback(async () => {
    await sharedData.onPersist({}, { preservePosition: true });

    trackEvent(events.ADD_COLOR_SCHEME_CLICKED, {
      triggerId: triggerIds.ELEMENT_SETTINGS,
    });

    dialogs.operations.show(dialogTypes.ADD_COLOR_SCHEME_DIALOG, {
      triggerId: triggerIds.ELEMENT_SETTINGS,
      surfaceAttribute,
      elementIds,
    });
  }, [sharedData, elementIds, surfaceAttribute]);

  // After returning from the add-color-scheme flow, internal state is restored from the
  // pre-flow snapshot, but the live DOM has been updated via UPDATE_LIVE_ONLY. Detect any
  // per-element drift between live DOM and state and re-emit it back into state.
  // skipTracking is set because this sync is not user-initiated.
  useEffect(() => {
    elementIds.forEach((elementId) => {
      const liveValue =
        view.accessors.getLiveElement(elementId)?.getAttribute(surfaceAttribute) || '';
      const stateValue = state[elementId]?.[surfaceAttribute] || '';

      if (liveValue === stateValue) return;

      onChange({
        controlId: id,
        elementIds: [elementId],
        attributes,
        value: liveValue || false,
        skipTracking: true,
      });
    });
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const captions = getCaptions();

  return (
    <Box
      paddingTop="spacing-xs"
      paddingLeft="spacing-m"
      paddingRight="spacing-m"
      paddingBottom={isToggleEnabled ? 'spacing-s' : 'spacing-xs'}
    >
      <ToggleSection
        value={isToggleEnabled}
        title={title}
        {...(popover && { popoverProps: popover })}
        onChange={handleToggleChange}
      >
        <ColorTileSelector
          value={currentValue}
          options={updatedChoices}
          indeterminateToolTipText={captions.indeterminateToolTipText}
          addButtonTooltipText={captions.addColorSchemeTooltipText}
          toggleLabels={captions.toggleLabels}
          onChange={handleColorSchemeChange}
          onConfigureClick={handleConfigureClick}
          onAddButtonClick={handleAddColorScheme}
        />
      </ToggleSection>
    </Box>
  );
}

ColorSchemeControl.propTypes = {
  id: PropTypes.string.isRequired,
  elementIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  attributes: PropTypes.arrayOf(PropTypes.string).isRequired,
  title: PropTypes.string.isRequired,
  choices: PropTypes.array.isRequired,
  popover: PropTypes.shape({
    title: PropTypes.string,
    imgSrc: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
};

export default ColorSchemeControl;
